import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { PillRadioGroup } from '@/shared/components/custom/pill-radio-group';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';

const FORMAT_OPTIONS = ['csv', 'excel', 'pdf'] as const;
type ExportFormat = (typeof FORMAT_OPTIONS)[number];

interface ExportAbsencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scopeRangeLabel: string;
  scopeTypeLabel: string;
}

/**
 * "Exportar" modal on the "Saldos" panel — UI-complete per the confirmed
 * design, but the final submit stays disabled: there is no export endpoint
 * (CSV/Excel/PDF) in `api.json` yet. See `tasks/current-story.md` for the
 * backend request.
 */
export function ExportAbsencesDialog({
  open,
  onOpenChange,
  scopeRangeLabel,
  scopeTypeLabel,
}: ExportAbsencesDialogProps) {
  const { t } = useTranslation('timeOff');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [includeWithoutHireDate, setIncludeWithoutHireDate] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-export-absences-dialog">
        <DialogHeader>
          <DialogTitle>{t('team.balances.exportDialog.title')}</DialogTitle>
          <DialogDescription>{t('team.balances.exportDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium">{t('team.balances.exportDialog.format')}</p>
            <PillRadioGroup
              value={format}
              onValueChange={(value) => setFormat(value as ExportFormat)}
              options={FORMAT_OPTIONS.map((value) => ({ value, label: value.toUpperCase() }))}
              data-testid="time-off-export-format"
            />
          </div>

          <div
            className="rounded-md bg-muted p-3 text-xs text-muted-foreground"
            data-testid="time-off-export-scope"
          >
            <p className="font-medium text-foreground">{t('team.balances.exportDialog.scope')}</p>
            <p>
              {t('team.balances.exportDialog.scopeSummary', {
                range: scopeRangeLabel,
                type: scopeTypeLabel,
              })}
            </p>
            <p className="mt-1">{t('team.balances.exportDialog.scopeHint')}</p>
          </div>

          <ToggleSwitch
            id="export-include-without-hire-date"
            checked={includeWithoutHireDate}
            onCheckedChange={setIncludeWithoutHireDate}
            label={t('team.balances.exportDialog.includeMembersWithoutHireDate')}
            data-testid="time-off-export-include-without-hire-date"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('team.balances.exportDialog.cancel')}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button type="button" disabled data-testid="time-off-export-submit-btn">
                  {t('team.balances.exportDialog.submit')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('team.balances.exportComingSoon')}</TooltipContent>
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
